import { useState, useRef, useEffect } from "react";
import Icon from "../Icon";

// Desplegable de modificaciones recientes.
//
// Cada fila de project_history es UN CAMPO cambiado, asi que aqui no hay que
// volver a comparar nada: el texto se compone leyendo campo/antes/despues. Por
// eso el historial se guarda asi en la base de datos.
const ACCION = {
  crear:       { icon: "plus-lg",    color: "#22c55e", verbo: "creo" },
  editar:      { icon: "pencil",     color: "#0b7d96", verbo: "cambio" },
  borrar:      { icon: "trash3",     color: "#ef4444", verbo: "elimino" },
  mover:       { icon: "arrows-fullscreen", color: "#8b5cf6", verbo: "movio" },
  asignar:     { icon: "person-check-fill", color: "#f59e0b", verbo: "asigno" },
  desasignar:  { icon: "x-lg",       color: "var(--text3)", verbo: "quito" },
};

const ENTIDAD = {
  proyecto: "el proyecto", tarea: "el paso", miembro: "a",
  asignacion: "las asignaciones de", dependencia: "la conexion",
};

// "hace 5 min" / "ayer" / "12 mar". Fechas cortas: la columna es estrecha y una
// fecha larga tapa el propio cambio.
function haceCuanto(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const min = Math.round((Date.now() - t) / 60000);
  if (min < 1)   return "ahora";
  if (min < 60)  return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24)    return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d === 1)   return "ayer";
  if (d < 7)     return `hace ${d} dias`;
  return new Date(t).toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

function frase(h) {
  const quien = h.user_name || "Alguien";
  const acc = ACCION[h.accion] || ACCION.editar;
  const que = h.task_title || h.etiqueta || ENTIDAD[h.entidad] || "";
  if (h.accion === "editar" && h.campo) {
    const de = h.antes ? `de ${h.antes} ` : "";
    return `${quien} cambio ${h.campo} ${de}a ${h.despues ?? "vacio"}${que ? ` en ${que}` : ""}`;
  }
  return `${quien} ${acc.verbo} ${ENTIDAD[h.entidad] || ""} ${que}`.replace(/\s+/g, " ").trim();
}

export default function HistorialReciente({ historial = [], onCargar, conProyecto = true }) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);

  // Cerrar al pulsar fuera y con Escape: el desplegable tapa las tarjetas.
  useEffect(() => {
    if (!abierto) return;
    const fuera = e => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    const esc   = e => { if (e.key === "Escape") setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", esc);
    };
  }, [abierto]);

  const alternar = () => {
    const v = !abierto;
    setAbierto(v);
    if (v) onCargar?.();     // se pide al abrir, no en cada render
  };

  return (
    <div ref={caja} style={{ position: "relative" }}>
      <button className="btn" onClick={alternar} title="Modificaciones recientes"
        style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
          fontFamily: "var(--font)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          border: `1px solid ${abierto ? "var(--accent)" : "var(--border)"}`,
          background: abierto ? "var(--accent-bg)" : "var(--surface)",
          color: abierto ? "var(--accent)" : "var(--text2)" }}>
        <Icon name="arrow-counterclockwise" size={12}
          color={abierto ? "var(--accent)" : "var(--text3)"} />
        Actividad
        <Icon name={abierto ? "chevron-up" : "chevron-down"} size={9}
          color={abierto ? "var(--accent)" : "var(--text3)"} />
      </button>

      {abierto && (
        <div className="fi" style={{ position: "absolute", top: "calc(100% + 5px)", right: 0,
          width: 330, maxHeight: 400, overflowY: "auto", zIndex: 400,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 11, boxShadow: "var(--shadow-lg)", padding: 6 }}>
          {!historial.length && (
            <div style={{ padding: "18px 12px", textAlign: "center", fontSize: 12,
              color: "var(--text3)" }}>
              Todavia no hay movimientos
            </div>
          )}
          {historial.map(h => {
            const acc = ACCION[h.accion] || ACCION.editar;
            return (
              <div key={h.id} style={{ display: "flex", gap: 8, padding: "8px 9px",
                borderBottom: "1px solid var(--border2)", alignItems: "flex-start" }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                  background: acc.color + "18", display: "flex", alignItems: "center",
                  justifyContent: "center", marginTop: 1 }}>
                  <Icon name={acc.icon} size={10} color={acc.color} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 11.5, lineHeight: 1.45, color: "var(--text2)" }}>
                    {frase(h)}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2,
                    display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span>{haceCuanto(h.created_at)}</span>
                    {conProyecto && h.project_name && (
                      <span style={{ color: "var(--accent)" }}>· {h.project_name}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
